import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  DateField,
  ReferenceField,
  FilterLiveSearch,
  SelectInput,
  DateInput,
  BooleanInput,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
} from 'react-admin';
import { Chip } from '@mui/material';
import { createEmptyStateComponent } from '../../components/createEmptyStateComponent';

const serviceTypes = [
  { id: 'OIL_CHANGE', name: 'Oil Change' },
  { id: 'INSPECTION', name: 'Inspection' },
  { id: 'REPAIR', name: 'Repair' },
  { id: 'TIRE_SERVICE', name: 'Tire Service' },
  { id: 'BRAKE_SERVICE', name: 'Brake Service' },
  { id: 'ENGINE_SERVICE', name: 'Engine Service' },
  { id: 'TRANSMISSION_SERVICE', name: 'Transmission Service' },
  { id: 'ELECTRICAL', name: 'Electrical' },
  { id: 'BODYWORK', name: 'Bodywork' },
  { id: 'PREVENTIVE', name: 'Preventive Maintenance' },
  { id: 'EMERGENCY', name: 'Emergency Repair' },
  { id: 'OTHER', name: 'Other' },
];

const ServiceTypeField = () => {
  const record = useRecordContext();
  if (!record?.serviceType) {
    return null;
  }

  const getColor = (type: string) => {
    switch (type) {
      case 'PREVENTIVE':
        return 'success';
      case 'EMERGENCY':
        return 'error';
      case 'REPAIR':
        return 'warning';
      case 'INSPECTION':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={record.serviceType.replace(/_/g, ' ')}
      color={getColor(record.serviceType)}
      size="small"
    />
  );
};

const CostField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  const value = record?.[source];

  if (!value) {
    return <span>-</span>;
  }

  return <span>${Number(value).toLocaleString()}</span>;
};

const MaintenanceEmptyState = createEmptyStateComponent('maintenance');

const MaintenanceListActions = () => (
  <TopToolbar>
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

const maintenanceFilters = [
  <FilterLiveSearch key="search" source="description" placeholder="Search maintenance..." />,
  <SelectInput
    key="type"
    source="serviceType"
    choices={serviceTypes}
    label="Service Type"
    alwaysOn
  />,
  <DateInput key="date" source="serviceDate" label="Service Date" />,
  <BooleanInput key="scheduled" source="scheduledService" label="Scheduled Only" />,
  <BooleanInput key="warranty" source="underWarranty" label="Under Warranty" />,
];

export const EquipmentMaintenanceList = () => (
  <List
    perPage={25}
    sort={{ field: 'serviceDate', order: 'DESC' }}
    filters={maintenanceFilters}
    actions={<MaintenanceListActions />}
    empty={<MaintenanceEmptyState />}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="id" sortable={false} />
      <ReferenceField source="equipmentId" reference="equipment">
        <TextField source="name" />
      </ReferenceField>
      <DateField source="serviceDate" />
      <ServiceTypeField />
      <TextField source="serviceProvider" />
      <NumberField source="serviceMileage" />
      <NumberField source="serviceEngineHours" />
      <CostField source="totalCost" />
      <BooleanField source="scheduledService" />
      <BooleanField source="underWarranty" />
      <DateField source="nextServiceDue" />
      <DateField source="createdAt" />
    </Datagrid>
  </List>
);
